import axios from 'axios';
import config from '../config/config';

const api = axios.create({
  baseURL: config.API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false, // Set to false to avoid CORS preflight issues
  timeout: 10000, // 10 second timeout
});

// Add request interceptor for debugging
api.interceptors.request.use(
  (config) => {
    // console.log('🚀 API Request:', config.method.toUpperCase(), config.url);
    // console.log('📍 Full URL:', config.baseURL + config.url);
    
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging
api.interceptors.response.use(
  (response) => {
    console.log('✅ API Response:', response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error('❌ API Error:', error.message);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    }
    
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (credentials) => {
    console.log('🔐 Attempting login with:', credentials.email);
    return api.post('/auth/login', credentials);
  },
  register: (userData) => api.post('/auth/register', userData),
};

// Painting APIs
export const paintingAPI = {
  getAll: (params) => api.get('/paintings', { params }),
  getById: (id) => api.get(`/paintings/${id}`),
  create: (formData) => api.post('/paintings', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  update: (id, formData) => api.put(`/paintings/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  delete: (id) => api.delete(`/paintings/${id}`),
  placeBid: (bidData) => api.post('/paintings/bid', bidData),
  analyze: (formData) => api.post('/paintings/analyze', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  contactSubmit: async (data) => {
    try {
      const response = await api.post('/contact', data);
      return response.data;
    } catch (error) {
      // Throw the error to be caught in the calling function
      throw error.response ? error.response.data : new Error('Network Error');
    }
  },
};

// Request APIs
export const requestAPI = {
  getAll: () => api.get('/requests'),
  create: (formData) => api.post('/requests', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  updateStatus: (id, status) => api.put(`/requests/${id}`, { status }),
  delete: (id) => api.delete(`/requests/${id}`),
};

// Order APIs
export const orderAPI = {
  getAll: () => api.get('/orders'),
  create: (orderData) => api.post('/orders', orderData),
  updateStatus: (id, statusData) => api.put(`/orders/${id}`, statusData),
};

// Auction APIs
export const auctionAPI = {
  getAll: () => api.get('/auctions'),
  placeBid: (id, bidData) => api.post(`/auctions/${id}/bid`, bidData),
  create: async (data) => {
    return await api.post('/auctions', data);
  },
  delete: async (id) => {
    return await api.delete(`/auctions/${id}`);
  },
};

// Blog APIs
export const blogAPI = {
  getAll: (params) => api.get('/blog', { params }),
  getBySlug: (slug) => api.get(`/blog/slug/${slug}`),
  getRelated: (slug) => api.get(`/blog/related/${slug}`),
  getCategories: () => api.get('/blog/categories'),
  getAllAdmin: () => api.get('/blog/admin/all'),
  create: (formData) => api.post('/blog', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  update: (id, formData) => api.put(`/blog/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  delete: (id) => api.delete(`/blog/${id}`),
};

export default api;